import React from "react";
import BrandLogo from "./BrandLogo";
import "./AppErrorBoundary.css";
import { t } from "../i18n";
import { hideBootSpinner } from "../lib/bootSpinner";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * The app-wide safety net (build 41). AdminErrorBoundary has covered the admin
 * dashboard for a while; the talent-facing app had nothing.
 *
 * Why it matters MORE on the phone than on the web: when a render error
 * escapes, React unmounts the whole tree and #root goes empty. No CSS in this
 * app paints `body`, and capacitor.config sets no webview background, so an
 * empty #root on an iOS phone in dark mode is `UIColor.systemBackground` —
 * PURE BLACK, with no way out but force-quitting the app. That is the dead end
 * CLAUDE.md's "cover EVERY path" rule exists to prevent: whatever the crash
 * was, the talent gets a painted screen and one tap back into the app.
 *
 * Reload (not a router navigation) on purpose: the tree that just threw is
 * gone, and a fresh boot re-runs AuthContext, which restores the session and
 * picks up any half-finished Stripe round trip (StripeSelfCompletion).
 */
export default class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("App crashed:", error, info.componentStack);
    // The boot screen sits on top of everything: get it out of the way of the
    // Reload button.
    hideBootSpinner();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-crash">
          <BrandLogo variant="light" alt="" className="app-crash-logo" />
          <h1 className="app-crash-title">{t("appErrorBoundary.letSTryThat")}</h1>
          <button className="app-crash-button" onClick={() => window.location.reload()}>
            {t("appErrorBoundary.reloadTheApp")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
